import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  TrendingUp,
  BarChart,
  Award,
  LineChart,
  AlertTriangle,
  Target,
  Zap,
  Briefcase,
  ChevronRight,
  Users,
  CheckCircle,
  XCircle,
  MessageSquare,
} from "lucide-react";
import { Alert } from "@mui/material";
import CompetitiveAnalysisService from "../../../../services/CompetitiveAnalysis";
import { config } from "../../../../config";
import AIDocumentQA from "../../../../components/AIDocumentQA";
import SendFeedbackDialog from "../../../../components/SendFeedbackDialog";
import { useSelector } from "react-redux";

const CompetitiveAnalysisDetailView = () => {
  const { projectId, analysisId } = useParams();
  const navigate = useNavigate();
  const reduxUser = useSelector((state: any) => state.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState<any>(null);
  const [parsedData, setParsedData] = useState<any>({
    initialAnalysis: null,
    competitorAnalysis: null,
    recommendations: null,
  });
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const newCompetitiveAnalysisServiceInstance = useMemo(
    () => new CompetitiveAnalysisService(),
    []
  );

  const parseAnalysisData = useCallback((analysisData: any) => {
    try {
      setParsedData({
        initialAnalysis: JSON.parse(analysisData.analysis.initialAnalysis),
        competitorAnalysis: JSON.parse(
          analysisData.analysis.competitorAnalysis
        ),
        recommendations: JSON.parse(analysisData.analysis.recommendations),
      });
    } catch (error) {
      console.error("Error parsing analysis data:", error);
      setError("Error parsing analysis data. Please try again.");
    }
  }, []);

  const fetchAnalysisDetails = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (!config.inProd) {
        const mockAnalysis = config.mocks.competitiveAnalysisReports.find(
          (report) => report._id === analysisId
        );
        if (!mockAnalysis) {
          throw new Error("Analysis not found");
        }
        setAnalysis(mockAnalysis);
        parseAnalysisData(mockAnalysis);
        return;
      }

      const service = newCompetitiveAnalysisServiceInstance;
      const response = await service.getAnalysis(projectId, analysisId);
      setAnalysis(response);
      parseAnalysisData(response);
    } catch (error: any) {
      console.error("Error fetching analysis details:", error);
      setError(error.message || "Failed to load analysis details");
    } finally {
      setLoading(false);
    }
  }, [
    projectId,
    analysisId,
    parseAnalysisData,
    newCompetitiveAnalysisServiceInstance,
  ]);

  useEffect(() => {
    fetchAnalysisDetails();
  }, [fetchAnalysisDetails]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            {/* Header skeleton */}
            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                <div className="space-y-3 flex-1">
                  <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>

            {/* Content skeletons */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="h-40 bg-gray-200 rounded mb-4"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-6 h-64">
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 h-64">
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
                <div className="space-y-4">
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis || !parsedData.initialAnalysis) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm max-w-md border border-gray-200">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <AlertTriangle className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No analysis found
          </h3>
          <p className="mb-6 text-gray-600">
            The requested analysis could not be found or loaded.
          </p>
          <button
            onClick={() =>
              navigate(`/project/${projectId}/competitive-analysis`)
            }
            className="flex items-center justify-center mx-auto gap-2 text-red-600 hover:text-red-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analyses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 break-words">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() =>
                  navigate(`/project/${projectId}/competitive-analysis`)
                }
                className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="min-w-0 break-words">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
                  {analysis.name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 break-words">
                  Created {new Date(analysis.createdAt).toLocaleDateString()} •{" "}
                  {parsedData.competitorAnalysis?.competitors?.length || 0}{" "}
                  competitors
                </p>
              </div>
            </div>

            <div className="w-full sm:w-auto flex gap-3 items-center break-words">
              <button
                onClick={() => setShowFeedbackDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg shadow-sm border border-gray-200 transition-all duration-200"
              >
                <MessageSquare className="w-4 h-4" />
                Feedback
              </button>
              <AIDocumentQA
                userId={reduxUser.id}
                documentName={`Analysis ${analysis._id}`}
                projectId={projectId}
                documentId={analysisId}
                documentType="Analysis"
                service={newCompetitiveAnalysisServiceInstance}
              />
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" className="rounded-lg">
            {error}
          </Alert>
        )}

        {/* Market Overview Section */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="p-1.5 sm:p-2 rounded-full bg-red-50">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Market Overview
              </h2>
            </div>

            {/* Summary Section */}
            {parsedData.initialAnalysis.summary && (
              <div className="mb-6">
                <h3 className="text-base sm:text-lg font-semibold mb-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-50 rounded-lg border-l-4 border-red-500">
                  Summary
                </h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4 px-2 sm:px-4">
                  {parsedData.initialAnalysis.summary.overallPosition}
                </p>

                <div className="bg-red-50 p-3 sm:p-4 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-2 flex items-center gap-1.5">
                    <Award className="h-4 w-4 text-red-600" />
                    Key Findings
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                    {parsedData.initialAnalysis.summary.keyFindings.map(
                      (finding: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-start gap-2 bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                        >
                          <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700">
                            {finding}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Market Opportunities */}
            {parsedData.initialAnalysis.marketOpportunities && (
              <div className="mt-6">
                <h3 className="text-base sm:text-lg font-semibold mb-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-50 rounded-lg border-l-4 border-red-500">
                  Market Opportunities
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-3 sm:gap-4">
                  {/* Immediate Opportunities */}
                  <div className="bg-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-100">
                    <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-1.5">
                      <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      Immediate Opportunities
                    </h4>
                    <div className="space-y-3 sm:space-y-4">
                      {parsedData.initialAnalysis.marketOpportunities.immediateOpportunities.map(
                        (opportunity: any, index: number) => (
                          <div
                            key={index}
                            className="bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                          >
                            <p className="font-medium text-gray-900 mb-1.5 pb-1.5 border-b border-gray-100 text-sm sm:text-base">
                              {opportunity.opportunity}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 mt-2">
                              {[
                                {
                                  label: "Impact",
                                  value: opportunity.potentialImpact,
                                },
                                {
                                  label: "Timeline",
                                  value: opportunity.timeToRealize,
                                },
                                {
                                  label: "Resources",
                                  value:
                                    opportunity.requiredResources.join(", "),
                                },
                              ].map((item, idx) => (
                                <div
                                  key={idx}
                                  className="bg-blue-50 p-1 sm:p-1.5 rounded flex flex-col items-center break-words"
                                >
                                  <span className="text-xs text-blue-600 font-medium">
                                    {item.label}
                                  </span>
                                  <span className="text-xs sm:text-sm text-gray-700">
                                    {item.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Long Term Opportunities */}
                  <div className="bg-green-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-green-100">
                    <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-1.5">
                      <Target className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                      Long Term Opportunities
                    </h4>
                    <div className="space-y-3 sm:space-y-4">
                      {parsedData.initialAnalysis.marketOpportunities.longTermOpportunities.map(
                        (opportunity: any, index: number) => (
                          <div
                            key={index}
                            className="bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                          >
                            <p className="font-medium text-gray-900 mb-1.5 pb-1.5 border-b border-gray-100 text-sm sm:text-base">
                              {opportunity.opportunity}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2 mt-2">
                              {[
                                {
                                  label: "Market Size",
                                  value: opportunity.marketSize,
                                },
                                {
                                  label: "Timeline",
                                  value: opportunity.timelineToMarket,
                                },
                                {
                                  label: "Barriers",
                                  value:
                                    opportunity.potentialBarriers.join(", "),
                                },
                              ].map((item, idx) => (
                                <div
                                  key={idx}
                                  className="bg-green-50 p-1 sm:p-1.5 rounded flex flex-col items-center break-words"
                                >
                                  <span className="text-xs text-green-600 font-medium">
                                    {item.label}
                                  </span>
                                  <span className="text-xs sm:text-sm text-gray-700">
                                    {item.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Competitor Analysis Section */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="p-1.5 sm:p-2 rounded-full bg-purple-50">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Competitor Analysis
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {parsedData.competitorAnalysis.competitors.map(
                (competitor: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm"
                  >
                    {/* Competitor Header */}
                    <div className="bg-gradient-to-r from-red-600 to-gray-900 px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <h3 className="text-lg sm:text-xl font-bold text-white truncate">
                          {competitor.name}
                        </h3>
                        <div className="bg-white bg-opacity-20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                          Market Share: {competitor.profileAnalysis.marketShare}
                        </div>
                      </div>
                    </div>

                    {/* Competitor Content */}
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
                        {/* Company Profile */}
                        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                            <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                            Company Profile
                          </h4>
                          <div className="space-y-2 sm:space-y-3">
                            {[
                              {
                                label: "Revenue",
                                value:
                                  competitor.profileAnalysis.revenueEstimate,
                              },
                              {
                                label: "Markets",
                                value: competitor.profileAnalysis.targetMarkets,
                              },
                              {
                                label: "Geographic Presence",
                                value:
                                  competitor.profileAnalysis.geographicPresence,
                              },
                            ].map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 p-2 sm:p-3 bg-white rounded-lg shadow-sm"
                              >
                                <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5"></div>
                                <div className="flex-1">
                                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                                    {item.label}:
                                  </span>
                                  {Array.isArray(item.value) ? (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {item.value.map(
                                        (val: string, i: number) => (
                                          <span
                                            key={i}
                                            className="inline-block px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs"
                                          >
                                            {val}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-xs sm:text-sm text-gray-800">
                                      {item.value}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Product Offering */}
                        <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-100">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
                            <LineChart className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                            Product Offering
                          </h4>
                          <div className="space-y-2 sm:space-y-3">
                            {[
                              {
                                label: "Main Products",
                                value: competitor.productOffering.mainProducts,
                              },
                              {
                                label: "Unique Features",
                                value:
                                  competitor.productOffering.uniqueFeatures,
                              },
                              {
                                label: "Pricing Strategy",
                                value:
                                  competitor.productOffering.pricingStrategy,
                              },
                            ].map((item, idx) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 p-2 sm:p-3 bg-white rounded-lg shadow-sm"
                              >
                                <div className="w-2 h-2 bg-red-400 rounded-full mt-1.5"></div>
                                <div className="flex-1">
                                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                                    {item.label}:
                                  </span>
                                  {Array.isArray(item.value) ? (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {item.value.map(
                                        (val: string, i: number) => (
                                          <span
                                            key={i}
                                            className="inline-block px-2 py-1 bg-red-50 text-red-700 rounded text-xs"
                                          >
                                            {val}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-xs sm:text-sm text-gray-800">
                                      {item.value}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Strengths & Weaknesses */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <div className="bg-green-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-green-100">
                          <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-1.5">
                            <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                            Strengths
                          </h4>
                          <div className="space-y-2 sm:space-y-3">
                            {competitor.strengths.map(
                              (strength: any, idx: number) => (
                                <div
                                  key={idx}
                                  className="bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                                >
                                  <div className="flex items-center">
                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-medium">
                                      Impact: {strength.impactLevel}
                                    </span>
                                  </div>
                                  <p className="text-xs sm:text-sm text-gray-700 mt-2">
                                    {strength.description}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        <div className="bg-red-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-red-100">
                          <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-1.5">
                            <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                            Weaknesses
                          </h4>
                          <div className="space-y-2 sm:space-y-3">
                            {competitor.weaknesses.map(
                              (weakness: any, idx: number) => (
                                <div
                                  key={idx}
                                  className="bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                                >
                                  <div className="flex items-center">
                                    <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                                      Exploitability:{" "}
                                      {weakness.exploitabilityScore}
                                    </span>
                                  </div>
                                  <p className="text-xs sm:text-sm text-gray-700 mt-2">
                                    {weakness.description}
                                  </p>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Strategic Recommendations Section */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="p-1.5 sm:p-2 rounded-full bg-blue-50">
                <BarChart className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Strategic Recommendations
              </h2>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {parsedData.recommendations.strategicPriorities.map(
                (priority: any, index: number) => (
                  <div
                    key={index}
                    className="bg-gradient-to-r from-red-50 to-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-red-100 shadow-sm"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
                          {priority.priority}
                        </h3>
                        <div className="inline-flex items-center px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium">
                          {priority.timeframe}
                        </div>
                      </div>
                      <div className="bg-white h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center shadow-sm border border-blue-100">
                        <span className="text-blue-800 font-semibold text-sm sm:text-base">
                          {index + 1}
                        </span>
                      </div>
                    </div>

                    <div className="bg-white/60 backdrop-blur-sm p-3 sm:p-4 rounded-lg mb-4">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        Rationale
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {priority.rationale}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                        <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
                        Expected Outcomes
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {priority.expectedOutcomes.map(
                          (outcome: any, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-start gap-2 bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                            >
                              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <CheckCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-600" />
                              </div>
                              <p className="text-xs sm:text-sm text-gray-700">
                                {outcome}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Resource Allocation Section */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="p-1.5 sm:p-2 rounded-full bg-purple-50">
                <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Resource Allocation
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {parsedData.recommendations.resourceAllocation.recommended.map(
                (item: any, index: number) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3">
                      <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                        {item.area}
                      </h3>
                      <div className="bg-gradient-to-r from-red-600 to-gray-900 text-white px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium shadow-sm">
                        {item.allocation}
                      </div>
                    </div>

                    <div className="mb-3 pb-3 border-b border-gray-100">
                      <p className="text-xs sm:text-sm text-gray-600">
                        {item.justification}
                      </p>
                    </div>

                    <div className="bg-red-50 p-2 sm:p-3 rounded-lg flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-red-800 font-medium">
                        Expected Return
                      </span>
                      <span className="text-xs sm:text-sm bg-white px-2.5 py-1 rounded-full text-red-900 font-medium shadow-sm border border-red-100">
                        {item.expectedReturn}
                      </span>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Market Threats Section */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="p-1.5 sm:p-2 rounded-full bg-red-50">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Market Threats
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {parsedData?.initialAnalysis?.marketThreats.map(
                (threat: any, index: number) => (
                  <div
                    key={index}
                    className="rounded-lg sm:rounded-xl overflow-hidden shadow-sm border border-red-100"
                  >
                    <div className="bg-gradient-to-r from-red-600 to-rose-500 p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <h3 className="text-base sm:text-lg font-semibold text-white truncate">
                          {threat.threat}
                        </h3>
                        <div className="flex gap-1.5">
                          <span className="px-2.5 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium border border-white/30">
                            {threat.severity.toUpperCase()}
                          </span>
                          <span className="px-2.5 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-xs font-medium border border-white/30">
                            {threat.likelihood.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 sm:p-4 bg-red-50">
                      <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm mb-4">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                          Potential Impact
                        </h4>
                        <p className="text-xs sm:text-sm text-red-800">
                          {threat.potentialImpact}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-xs sm:text-sm font-medium text-red-900 mb-2 flex items-center gap-1.5">
                          <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-600" />
                          Mitigation Strategies
                        </h4>
                        <div className="space-y-2 sm:space-y-3">
                          {threat.mitigationStrategies.map(
                            (strategy: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 bg-white p-2 sm:p-3 rounded-lg shadow-sm"
                              >
                                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <CheckCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-red-600" />
                                </div>
                                <p className="text-xs sm:text-sm text-gray-700">
                                  {strategy}
                                </p>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      <SendFeedbackDialog
        open={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        userId={reduxUser.id}
        projectId={projectId}
        documentId={analysisId}
        documentType="competitiveanalysis"
        documentName={analysis?.name || "Competitive Analysis"}
      />
    </div>
  );
};

export default CompetitiveAnalysisDetailView;
